import { useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { useCallback } from 'react';

/**
 * Custom hook for drag and drop functionality
 * Handles course reordering within periods
 * @param {Object} groupedCourses - All grouped courses
 * @param {Function} setGroupedCourses - Function to update grouped courses
 * @param {Object} courseOrders - Course orders per period
 * @param {Function} setCourseOrders - Function to update course orders
 * @returns {Object} - Drag and drop handlers and sensors
 */
export const useDragAndDrop = (
  groupedCourses, 
  setGroupedCourses, 
  courseOrders, 
  setCourseOrders
) => {
  // Configure drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  /**
   * Handle drag end event
   * @param {Object} event - Drag event from DndKit
   * @param {string} key - Period key where the drag occurred
   */
  const handleDragEnd = useCallback((event, key) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = groupedCourses[key].courses.findIndex((course) => {
      const courseId = course.is_split_course 
        ? `${course.semester_instance}_p${course.actual_period}` 
        : course.semester_instance;
      return courseId === active.id;
    });
    
    const newIndex = groupedCourses[key].courses.findIndex((course) => {
      const courseId = course.is_split_course 
        ? `${course.semester_instance}_p${course.actual_period}` 
        : course.semester_instance;
      return courseId === over.id;
    });

    if (oldIndex === -1 || newIndex === -1) return;

    const updatedCourses = arrayMove(groupedCourses[key].courses, oldIndex, newIndex);

    // Update grouped courses with new order
    setGroupedCourses((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        courses: updatedCourses,
      },
    }));

    // Update course orders for URL serialization (future enhancement)
    const newOrder = updatedCourses.map(course => 
      course.is_split_course 
        ? `${course.semester_instance}_p${course.actual_period}`
        : course.semester_instance
    );
    
    setCourseOrders(prev => ({
      ...prev,
      [key]: newOrder
    }));
  }, [groupedCourses, setGroupedCourses, setCourseOrders]);

  return {
    sensors,
    handleDragEnd
  };
}; 